import type { JSX } from 'react';
import { useState } from 'react';
import { useCandidateVerificationActions } from '../hooks/useCandidateVerificationActions';
import type { Experience } from '@/features/experiences/types/experience.types';
import type { Verification } from '@/features/verifications/types/verification.types';
import { AddReferenceContactAction } from './AddReferenceContactAction';
import { CandidateVerificationFormDialog } from './CandidateVerificationFormDialog';
import { ReferenceContactCard } from './ReferenceContactCard';

interface CandidateExperienceVerificationsProps {
  experience: Experience;
  verifications: Verification[];
}

export function CandidateExperienceVerifications({
  experience,
  verifications,
}: CandidateExperienceVerificationsProps): JSX.Element {
  const { createVerification, deleteVerification, sendVerification, isSending } =
    useCandidateVerificationActions(experience.id);
  const [dialogOpen, setDialogOpen] = useState(false);
  const hasContacts = verifications.length > 0;
  const isLocked = verifications.some((verification) => verification.status !== 'DRAFT');

  return (
    <div className="space-y-3 bg-muted/50 p-4">
      {!hasContacts && (
        <p className="text-sm text-muted-foreground">
          Add someone who can confirm this work experience.
        </p>
      )}
      {hasContacts && (
        <ul className="space-y-2">
          {verifications.map((ver) => (
            <ReferenceContactCard
              key={ver.id}
              verification={ver}
              onDelete={deleteVerification}
              onSend={sendVerification}
              isSending={isSending}
            />
          ))}
        </ul>
      )}
      {!isLocked && (
        <AddReferenceContactAction
          hasContacts={hasContacts}
          onClick={(): void => setDialogOpen(true)}
        />
      )}
      <CandidateVerificationFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onCreate={createVerification}
        experience={experience}
      />
    </div>
  );
}
